<template>
  <div>
    <div v-if="Object.keys(providerWarnings).length > 0 && !debtMessage" id="provider-warnings">
      <b-notification
        v-for="(message, title) in providerWarnings"
        :key="title"
        type="is-warning"
        has-icon
        aria-close-label="Close message"
        class="m-5"
        :message="'<b>'+title+'</b><br/>'+message" />
    </div>
    <Spinner :is-loading="isLoading" />
    <div v-if="!isLoading">
      <div v-if="debtMessage" class="notification-content">
        <WalletBalance :client-balance="$store.state.paymentsModule.clientBalance" />
        <Message :message="debtMessage.message" :icon="debtMessage.icon" />
      </div>
      <div v-else class="card-item">
        <RelatedTransactions
          v-if="relatedTransactions && relatedTransactions.length > 0"
          :related-transactions="relatedTransactions"
          :payment-providers="paymentProviders"
          :cancel-related-transaction-button="checkUserFeature('element.pay-here.button.cancel-related-transaction') ||
            currentUser.user.rol === 'CLIENT' ? cancellingRelatedTransaction : 'disabled'"
          class="card-item"
          @open-cancel-transaction-modal="$emit('open-cancel-transaction-modal', $event)" />
        <div class="header">
          <div class="header-text">
            <h3>Selecciona las cuotas</h3>
          </div>
          <WalletBalance class="wallet-balance" :client-balance="$store.state.paymentsModule.clientBalance" />
        </div>
        <ClientOperations
          v-if="creditList.length !== 0"
          :payments-map="paymentsMap"
          :operations="creditList"
          :current-loading-payment="currentLoadingPayment"
          :all-check-boxes-are-disabled="allCheckBoxesAreDisabled"
          @change="onChange"
          @confirm-deselect-payment="$emit('confirm-deselect-payment', $event)" />
      </div>
    </div>
  </div>
</template>
<script>
/* eslint-disable no-console */
import { getClient, getClientDebt } from '@/application/services/api-clients/clients';
import {
  createTransaction, changeTransactionSelectedPayments, getClientsTransactions, cancelTransaction, getTransactionsProviders
} from '@/application/services/api-clients/transactions';
import Message from '@/ui/components/Message.vue';
import { dateToFormatedString, dateSubtract, setDateOfMonth } from '@/infrastructure/date';
import ClientOperations from '@/ui/components/internal/pay-here/ClientOperations.vue';
import WalletBalance from '@/ui/components/internal/pay-here/WalletBalance.vue';
import RelatedTransactions from '@/ui/components/internal/pay-here/RelatedTransactions.vue';
import Spinner from '@/ui/components/Spinner.vue';
import { debtToCreditList } from '@/domain/helpers';
import userFeatures from '@/ui/mixins/user-features';

export default {
  name: 'Select',
  components: { Message, ClientOperations, Spinner, RelatedTransactions, WalletBalance },
  // mixins: [userFeatures],
  data() {
    return {
      isLoading: true,
      allCheckBoxesAreDisabled: false,
      currentLoadingPayment: undefined,
      debtMessage: '',
      pendingTransaction: null,
      cancellingRelatedTransaction: 'enabled',
      creditList: [],
      paymentProviders: [],
      paymentsMap: {},
      relatedTransactions: [],
      currentUser: this.$store.state.currentUser,
      clientId: this.$store.state.currentUser.user.clientId ? this.$store.state.currentUser.user.clientId : this.$store.state.backOfficeClientId
    };
  },
  computed: {
    providerWarnings() {
      if (this.paymentProviders.length === 0) {
        return {};
      }
      return this.paymentProviders.reduce((acc, provider) => {
        if (provider.warning && provider.warning.message !== '') {
          acc[provider.warning.title || provider.name] = provider.warning.message;
        }
        return acc;
      }, {});
    }
  },
  watch: {
    pendingTransaction: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.$store.dispatch('deleteTransactionData');
        } else {
          this.$store.dispatch('setTransactionData', val);
        }
      }
    }
  },
  async mounted() {
    await this.loadData();
    this.$emit('enableNextButton', this.$store.state.paymentsModule.total !== 0);
    this.$emit('sendNameNextStep', 'payment-provider');
    this.$emit('sendNameBackStep', '');
    this.$parent.$on('cancel-transaction', this.cancelRelatedTransaction);
    this.$parent.$on('change', this.onChange);
  },
  beforeDestroy() {
    this.$parent.$off('cancel-transaction');
  },
  methods: {
    // Por algún motivo el mixins no funciona en este modulo.
    checkUserFeature(name) {
      return userFeatures.methods.checkUserFeature(name, this.$store);
    },
    async loadData() {
      // Define paymentsMap e isLoading
      this.paymentsMap = {};
      this.isLoading = true;
      try {
        // Obtiene deuda
        const { debt, clientBalance } = await getClientDebt(this.clientId);
        // Construye un arreglo con los id de trx asociados a la deuda
        const relatedTransactionsIds = this.getRelatedTransactionsIds(debt);
        // Define promesas
        const promises = [getTransactionsProviders(), getClientsTransactions(this.clientId)];
        // Si hay ids asociados a la deuda, agrega una promesa para obtener la trx
        if (relatedTransactionsIds.length > 0) {
          promises.push(getClientsTransactions(this.clientId, relatedTransactionsIds));
        }
        // Ejecuta promesas
        [this.paymentProviders, [this.pendingTransaction], this.relatedTransactions] = await Promise.all(promises);
        // Construye paymentsMap a partir de la transacción pendiente
        this.updatePaymentsMapWithPendingTransaction(debt);
        this.creditList = debtToCreditList(debt);
        this.$store.dispatch('setClientBalance', clientBalance);
        this.$store.dispatch('setDebt', this.creditList);
        if (this.creditList.length === 0) {
          this.debtMessage = this.getMessageCheckIn(this.currentUser.user);
        }
      } catch (error) {
        if (error.message.search('status code 400') !== -1) {
          this.debtMessage = {
            message: `Mantente atento a tu correo ${this.currentUser.user.email}.
            Pronto podrás pagar tus cuotas directamente por aquí.`,
            icon: 'checkin-waiting.svg'
          };
        } else {
          this.debtMessage = {
            message: 'Ocurrió un error tratando de obtener la deuda.',
            icon: 'data_maintenance.svg'
          };
        }
      }
      this.isLoading = false;
      this.$store.dispatch('setPaymentsMap', this.paymentsMap);
    },
    async updateClientBalance() {
      const { balance } = await getClient(this.clientId);
      await this.$store.dispatch('setClientBalance', balance);
    },
    async createOrChangeTransaction() {
      this.allCheckBoxesAreDisabled = true;
      const clientId = this.$store.state.currentUser.user.clientId ? this.$store.state.currentUser.user.clientId : this.$store.state.backOfficeClientId;
      // Obtiene el monto total seleccionado y un arreglo de objetos que representa la operación y las cuotas seleccionadas
      // { operationId: 'operationId', paymentsNumbers: [1, 2, 3] }
      const { totalAmount, selectedPayments } = Object.keys(this.paymentsMap).reduce((acc, operationId) => {
        if (this.paymentsMap[operationId].length === 0) {
          return acc;
        }
        const paymentsNumbers = this.paymentsMap[operationId].map(payment => {
          acc.totalAmount += payment.secondaryCurrencyTotalMonthlyPayment;
          return payment.paymentNumber;
        });
        acc.selectedPayments.push({ operationId, paymentsNumbers });
        return acc;
      }, { totalAmount: 0, selectedPayments: [] });
      // Si no existe transacción pendiente
      if (!this.pendingTransaction) {
        this.pendingTransaction = await createTransaction(clientId, selectedPayments, totalAmount);
        await this.updateClientBalance();
        this.allCheckBoxesAreDisabled = false;
        this.currentLoadingPayment = undefined;
        return;
      }
      // Si sí existe transacción pendiente
      const transactionId = this.pendingTransaction._id;
      // Existe una transaccion pendiente y se deseleccionó todo, hay que matarla
      if (selectedPayments.length === 0) {
        await cancelTransaction(transactionId);
        await this.updateClientBalance();
        this.pendingTransaction = null;
        this.allCheckBoxesAreDisabled = false;
        this.currentLoadingPayment = undefined;
        return;
      }
      // Existe una transacción pendiente y se cambió la selección, hay que actualizarla
      this.pendingTransaction = (
        await changeTransactionSelectedPayments(transactionId, selectedPayments, totalAmount)
      ).transaction;
      await this.updateClientBalance();
      this.allCheckBoxesAreDisabled = false;
      this.currentLoadingPayment = undefined;
    },
    async cancelRelatedTransaction(transactionId) {
      this.allCheckBoxesAreDisabled = true;
      this.cancellingRelatedTransaction = 'disabled';
      this.$emit('enableNextButton', false);
      try {
        await cancelTransaction(transactionId);
      } catch (_error) {
        this.debtMessage = { message: 'Ocurrió un error al comunicarse con el servidor',
          icon: 'data_maintenance.svg' };
      }
      await this.loadData();
      this.allCheckBoxesAreDisabled = false;
      this.cancellingRelatedTransaction = 'enabled';
      this.$emit('enableNextButton', true);
      this.$emit('open-cancel-transaction-modal', false);
    },
    // Construye un arreglo con los id de trx asociados a la deuda
    getRelatedTransactionsIds: debt => debt.reduce((accum, operation) => {
      return operation.payments.reduce((ac, payment) => {
        if (payment.isPending && !ac.includes(payment.transactionId)) {
          ac.push(payment.transactionId);
        }
        return accum;
      }, accum);
    }, []),
    // Determina si un payment está considerado en las transacciones relacionadas
    relatedTransactionsContainsPayment(payment) {
      return this.relatedTransactions === undefined
        ? false
        : this.relatedTransactions.some(relatedTransaction => {
          return relatedTransaction.selectedPayments.some(relatedOperation => {
            return relatedOperation.payments.some(relatedPayment => {
              const isSameOperation = payment.operationId === relatedOperation.operationId;
              const isSamePayment = payment.paymentNumber === relatedPayment.paymentNumber;
              const isPreviousPyment = payment.paymentNumber - 1 === relatedPayment.paymentNumber;
              return isSameOperation && (isSamePayment || isPreviousPyment);
            });
          });
        });
    },
    // Construye paymentsMap a partir de la transacción pendiente
    updatePaymentsMapWithPendingTransaction(debt) {
      if (debt.length === 0) {
        return;
      }
      if (this.pendingTransaction !== null && this.pendingTransaction !== undefined) {
        this.pendingTransaction.selectedPayments.forEach(transactionOperation => {
          // Busca la operación de la trx en la deuda
          const operationFromDebt = debt.find(x => x._id === transactionOperation.operationId);
          this.paymentsMap[transactionOperation.operationId] = transactionOperation.payments.map(x => {
            // Para cada cuota de la trx, asigna la cuota de la deuda en paymentsMap
            // Nota: con esto, no conservo el valor de la cuota al momento de creada la transacción, sino al momento de consultada la deuda
            return operationFromDebt.payments.find(paymentFromDebt => paymentFromDebt.paymentNumber === x.paymentNumber);
          });
        });
      }
    },
    async onChange(event) {
      // Bloquea el botón para evitar lanzar más de un llamado al backend
      this.$emit('enableNextButton', false);

      // Obtiene la referencia al evento, al pago asociado a la cuota y su operación
      const paymentsFromSelection = [];
      let operationFromSelection;

      const { checked, selection } = event;

      // agrega a paymentsFromSelection las cuotas seleccionadas para actualizar a partir de creditList
      selection.forEach(selectionSelectedPayment => {
        // verifica que la cuota no esté en pending transactions
        const isSelectionSelectedPaymentInRelatedTransactions = this.relatedTransactionsContainsPayment(selectionSelectedPayment);

        // busca operación y cuota dentro de credit list
        const { selectedOperation, selectedPayment } = this.findPaymentInCreditList(selectionSelectedPayment);

        // empuja los hallazgos
        if (!isSelectionSelectedPaymentInRelatedTransactions) { paymentsFromSelection.push(selectedPayment); }
        if (['MORTGAGE', 'SME', 'PARKING'].includes(selectionSelectedPayment.type)) {
          operationFromSelection = selectedOperation;
        } else if (selectionSelectedPayment.type === 'COMPLEMENTARY' && !operationFromSelection) {
          operationFromSelection = selectedOperation;
        }
      });

      this.currentLoadingPayment = `${operationFromSelection._id}-${paymentsFromSelection[0].paymentNumber}`;
      // Checked: agrega cuotas seleccionadas al paymentsMap
      if (checked === true) {
        paymentsFromSelection.forEach(payment => {
          // Si es necesario, crea un arreglo en paymentsMap con el id de operación
          if (!this.paymentsMap[payment.operationId]) { this.paymentsMap[payment.operationId] = []; }

          // Para cada operación, agrega a paymentsMap los payments que corresponda
          payment.operation.payments.forEach(p => {
            if (p.paymentNumber <= payment.paymentNumber) {
              const paymentIsAlreadyInPaymentsMap = this.paymentsMap[payment.operationId].some(x => x.paymentNumber === p.paymentNumber);
              const paymentIsAlreadyInRelatedTransactions = this.relatedTransactionsContainsPayment(p);
              if (!paymentIsAlreadyInPaymentsMap && !paymentIsAlreadyInRelatedTransactions) {
                this.paymentsMap[payment.operationId].push(p);
              }
            }
          });
        });
      } else {
        // Si al hacer click, deselecciona el checkbox
        // Quita de paymentsMap los paymentsNumbers >= al clickeado de esa operación
        paymentsFromSelection.forEach(payment => {
          const paymentIndex = this.paymentsMap[payment.operationId].findIndex(p => p.paymentNumber === payment.paymentNumber);
          this.paymentsMap[payment.operationId].splice(paymentIndex, this.paymentsMap[payment.operationId].length);
        });
      }

      // actualiza store, crea la transacción y, si aplica, habilita el botón next
      this.$store.dispatch('setPaymentsMap', this.paymentsMap);
      await this.createOrChangeTransaction();
      if (this.$store.state.paymentsModule.total !== 0) {
        this.$emit('enableNextButton', true);
      }
    },
    getMessageCheckIn(user) {
      // Cliente no tiene deuda porque está al día
      if (!user.checkIn || (!user.checkIn.required && user.checkIn.is) || user.nextMonthlyPayment) {
        const date = user.nextMonthlyPaymentType === 'SME' ? user.nextMonthlyPayment : dateSubtract(setDateOfMonth(user.nextMonthlyPayment, 20), 1, 'month');
        return {
          message: `No tienes cuotas pendientes de pago.
            Tu próxima cuota estará disponible a partir del
            ${dateToFormatedString(date, 'DD/MM/YYYY')}`,
          icon: 'sin-cuotas-pendientes.svg'
        };
      }
      // Cliente hizo check-in pero todavía no existe su operación
      return {
        message: `Mantente atento a tu correo ${user.email}. Pronto podrás pagar tus cuotas directamente por aquí.`,
        icon: 'checkin-waiting.svg'
      };
    },
    findPaymentInCreditList(selectionSelectedPayment) {
      return this.creditList.reduce((creditFound, credit) => {
        const { operationType, paymentType } = {
          MORTGAGE: {
            operationType: '_id',
            paymentType: 'mortgagePayment'
          },
          COMPLEMENTARY: {
            operationType: 'complementaryOperationId',
            paymentType: 'complementaryPayment'
          },
          SME: {
            operationType: '_id',
            paymentType: 'mortgagePayment'
          },
          PARKING: {
            operationType: '_id',
            paymentType: 'mortgagePayment'
          }
        }[selectionSelectedPayment.type];

        const isCurrentOperationInCurrentCredit = credit[operationType] === selectionSelectedPayment.operationId;
        if (!isCurrentOperationInCurrentCredit) { return creditFound; }

        const paymentToReturn = credit.allPayments.find(p => (p[paymentType] || {}).paymentNumber === selectionSelectedPayment.paymentNumber)[paymentType];
        const currentOperation = ['MORTGAGE', 'SME', 'PARKING'].includes(selectionSelectedPayment.type) ? credit : credit.complementaryOperation;
        paymentToReturn.operation = currentOperation;

        return {
          selectedOperation: currentOperation,
          selectedPayment: paymentToReturn
        };
      }, { selectedOperation: {}, selectedPayment: {} });
    }
  }
};
</script>
<style scoped>
.header {
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-content: center;
}
.notification-content {
  width: 95%;
  margin: 30px auto;
  display: flex;
  flex-flow: column wrap;
  gap: 1rem;
}
.card-item {
  margin: 2rem 0rem;
  padding: 2% 5%;
  overflow: visible;
}
</style>
